import {
  CircleHelpIcon,
  MessageSquareIcon,
  SparklesIcon,
  ExternalLinkIcon,
  LightbulbIcon,
} from '@buffer-mono/popcorn'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

declare global {
  interface Window {
    __openChangelog?: () => void
  }
}

/**
 * Builds help-related command bar actions (Help Center, Feedback, Changelog, etc.).
 * These actions are excluded from frecency tracking.
 *
 * @returns Array of help-related command bar actions
 */
export function buildHelpActions(): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  actions.push(
    createAction({
      id: 'help-center',
      name: 'Help Center',
      keywords: ['help', 'support', 'docs', 'documentation', 'faq', 'guide'],
      icon: <CircleHelpIcon />,
      section: 'help',
      priority: PRIORITIES.channelSubmenus,
      description: 'Browse help articles and guides',
      excludeFromFrecency: true,
      perform: () => {
        window.open('https://support.buffer.com', '_blank', 'noopener')
      },
    }),
  )

  actions.push(
    createAction({
      id: 'send-feedback',
      name: 'Send Feedback',
      keywords: ['feedback', 'suggestion', 'bug', 'report', 'contact'],
      icon: <MessageSquareIcon />,
      section: 'help',
      priority: PRIORITIES.sendFeedback,
      description: 'Share your thoughts with us',
      excludeFromFrecency: true,
      perform: () => {
        // Try feedback widget first, fall back to email
        const feedbackWidget = document.querySelector<HTMLElement>(
          '[data-feedback-widget-trigger]',
        )
        if (feedbackWidget) {
          feedbackWidget.click()
        } else {
          window.open(
            'mailto:hello@buffer.com?subject=Feedback',
            '_blank',
            'noopener',
          )
        }
      },
    }),
  )

  actions.push(
    createAction({
      id: 'whats-new',
      name: "What's New",
      keywords: ['whats new', 'changelog', 'updates', 'features', 'release'],
      icon: <SparklesIcon />,
      section: 'help',
      priority: PRIORITIES.channelGroups,
      description: 'See the latest Buffer updates',
      excludeFromFrecency: true,
      perform: () => {
        if (typeof window.__openChangelog === 'function') {
          window.__openChangelog()
        } else {
          window.open('https://buffer.com/changelog', '_blank', 'noopener')
        }
      },
    }),
  )

  actions.push(
    createAction({
      id: 'suggest-feature',
      name: 'Suggest a Feature',
      keywords: [
        'suggest',
        'feature',
        'idea',
        'request',
        'feedback',
        'product',
      ],
      icon: <LightbulbIcon />,
      section: 'help',
      priority: PRIORITIES.suggestFeature,
      description: 'Share your product ideas',
      excludeFromFrecency: true,
      perform: () => {
        window.open('https://suggestions.buffer.com', '_blank', 'noopener')
      },
    }),
  )

  actions.push(
    createAction({
      id: 'keyboard-shortcuts',
      name: 'Keyboard Shortcuts',
      keywords: ['keyboard', 'shortcuts', 'hotkeys', 'keys', 'bindings'],
      icon: <ExternalLinkIcon />,
      section: 'help',
      priority: PRIORITIES.keyboardShortcuts,
      description: 'View all keyboard shortcuts',
      excludeFromFrecency: true,
      perform: () => {
        window.open(
          'https://support.buffer.com/hc/en-us/articles/360037988834-Keyboard-Shortcuts',
          '_blank',
          'noopener',
        )
      },
    }),
  )

  return actions
}
